import { projectId, publicAnonKey } from './supabase/info';

const API_BASE_URL = `https://${projectId}.supabase.co/functions/v1/make-server-0b38861b`;

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  token?: string | null;
}

interface ClusterSnapshotQueryOptions {
  limit?: number;
  startDate?: string;
  endDate?: string;
  programCode?: string;
  programId?: string;
  label?: string;
  createdById?: string;
  clusterCount?: number;
}

export async function apiCall<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, token } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token || publicAnonKey}`
  };

  const config: RequestInit = {
    method,
    headers
  };

  if (body && (method === 'POST' || method === 'PUT')) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    
    if (!response.ok) {
      let errorMessage = 'An error occurred';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch (e) {
        errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    // Enhanced error handling
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error('Network error - Server function may not be deployed:', API_BASE_URL);
      throw new Error('Unable to connect to server. Please ensure the Supabase Edge Function is deployed.');
    }
    throw error;
  }
}

// Helper functions for common operations
export const api = {
  // Students
  getStudents: (token: string) => apiCall<{ students: any[] }>('/students', { token }),
  createStudent: (data: any, token: string) => apiCall('/students', { method: 'POST', body: data, token }),
  updateStudent: (id: string, data: any, token: string) => apiCall(`/students/${id}`, { method: 'PUT', body: data, token }),
  deleteStudent: (id: string, token: string) => apiCall(`/students/${id}`, { method: 'DELETE', token }),

  // Faculty
  getFaculty: (token: string) => apiCall<{ faculty: any[] }>('/faculty', { token }),
  createFaculty: (data: any, token: string) => apiCall('/faculty', { method: 'POST', body: data, token }),
  updateFaculty: (id: string, data: any, token: string) => apiCall(`/faculty/${id}`, { method: 'PUT', body: data, token }),
  deleteFaculty: (id: string, token: string) => apiCall(`/faculty/${id}`, { method: 'DELETE', token }),

  // Courses
  getCourses: (token: string) => apiCall<{ courses: any[] }>('/courses', { token }),
  getPublicCourses: () => apiCall<{ courses: any[] }>('/courses/public'),
  createCourse: (data: any, token: string) => apiCall('/courses', { method: 'POST', body: data, token }),
  updateCourse: (id: string, data: any, token: string) => apiCall(`/courses/${id}`, { method: 'PUT', body: data, token }),
  deleteCourse: (id: string, token: string) => apiCall(`/courses/${id}`, { method: 'DELETE', token }),

  // Subjects
  getSubjects: (token: string) => apiCall<{ subjects: any[] }>('/subjects', { token }),
  createSubject: (data: any, token: string) => apiCall('/subjects', { method: 'POST', body: data, token }),
  updateSubject: (id: string, data: any, token: string) => apiCall(`/subjects/${id}`, { method: 'PUT', body: data, token }),
  deleteSubject: (id: string, token: string) => apiCall(`/subjects/${id}`, { method: 'DELETE', token }),

  // Rooms
  getRooms: (token: string) => apiCall<{ rooms: any[] }>('/rooms', { token }),
  createRoom: (data: any, token: string) => apiCall('/rooms', { method: 'POST', body: data, token }),
  updateRoom: (id: string, data: any, token: string) => apiCall(`/rooms/${id}`, { method: 'PUT', body: data, token }),
  deleteRoom: (id: string, token: string) => apiCall(`/rooms/${id}`, { method: 'DELETE', token }),

  // Schedules
  getSchedules: (token: string) => apiCall<{ schedules: any[] }>('/schedules', { token }),
  getFacultySchedules: async (facultyId: string, token: string) => {
    // First try the dedicated faculty endpoint
    try {
      return await apiCall<{ schedules: any[] }>(`/schedules/faculty/${facultyId}`, { token });
    } catch (error) {
      // Fallback: If faculty endpoint doesn't exist, try to get all schedules and filter
      // This will only work for admins, but provides a temporary workaround
      console.log('Faculty schedules endpoint not available, using fallback');
      try {
        const allSchedules = await apiCall<{ schedules: any[] }>('/schedules', { token });
        // Filter schedules by faculty ID (note: this won't have enriched data)
        const filtered = allSchedules.schedules.filter((s: any) => s.facultyId === facultyId);
        return { schedules: filtered };
      } catch (fallbackError) {
        // If both fail, return empty schedules
        console.log('Fallback also failed, returning empty schedules');
        return { schedules: [] };
      }
    }
  },
  getFacultyClassStudents: (facultyId: string, scheduleId: string, token: string) =>
    apiCall<{ students: any[]; schedule: any }>(`/schedules/faculty/${facultyId}/classes/${scheduleId}/students`, { token }),
  createSchedule: (data: any, token: string) => apiCall('/schedules', { method: 'POST', body: data, token }),
  updateSchedule: (id: string, data: any, token: string) => apiCall(`/schedules/${id}`, { method: 'PUT', body: data, token }),
  deleteSchedule: (id: string, token: string) => apiCall(`/schedules/${id}`, { method: 'DELETE', token }),

  // Admins
  getAdmins: (token: string) => apiCall<{ admins: any[] }>('/admins', { token }),
  createAdmin: (data: any, token: string) => apiCall('/admins', { method: 'POST', body: data, token }),
  updateAdmin: (id: string, data: any, token: string) => apiCall(`/admins/${id}`, { method: 'PUT', body: data, token }),
  deleteAdmin: (id: string, token: string) => apiCall(`/admins/${id}`, { method: 'DELETE', token }),

  // User Directory (for sharing picker - any authenticated user)
  getUserDirectory: (token: string) => apiCall<{ faculty: any[]; students: any[] }>('/users/directory', { token }),

  // Archives
  getArchives: (token: string) => apiCall<{ archives: any[] }>('/archives', { token }),
  getArchiveStats: (token: string) => apiCall<{ stats: any }>('/archives/stats', { token }),
  createArchive: (data: any, token: string) => apiCall('/archives', { method: 'POST', body: data, token }),
  updateArchive: (id: string, data: any, token: string) => apiCall(`/archives/${id}`, { method: 'PUT', body: data, token }),
  deleteArchive: (id: string, token: string) => apiCall(`/archives/${id}`, { method: 'DELETE', token }),

  // Document Folders
  getFolders: (token: string) => apiCall<{ folders: any[] }>('/folders', { token }),
  createFolder: (data: any, token: string) => apiCall('/folders', { method: 'POST', body: data, token }),
  updateFolder: (id: string, data: any, token: string) => apiCall(`/folders/${id}`, { method: 'PUT', body: data, token }),
  deleteFolder: (id: string, token: string) => apiCall(`/folders/${id}`, { method: 'DELETE', token }),

  // Student Subject Offerings
  getStudentSubjects: (academicYear: string, semester: string, token: string, yearLevel?: number) => 
    apiCall<{ subjects: any[] }>(`/student/subjects?academicYear=${encodeURIComponent(academicYear)}&semester=${encodeURIComponent(semester)}${yearLevel ? `&yearLevel=${yearLevel}` : ''}`, { token }),

  // Enrollments
  getStudentEnrollments: (studentId: string, token: string) => 
    apiCall<{ enrollments: any[] }>(`/enrollments/student/${studentId}`, { token }),
  createEnrollments: (data: any, token: string) => 
    apiCall('/enrollments', { method: 'POST', body: data, token }),
  deleteEnrollment: (id: string, token: string) => 
    apiCall(`/enrollments/${id}`, { method: 'DELETE', token }),
    
  // Verification
  getPendingVerifications: (token: string) => apiCall<{ users: any[] }>('/admin/pending-verifications', { token }),
    
  // Public Stats (no auth required)
  getPublicStats: () => apiCall<{ stats: { archiveDocuments: number; graduatePrograms: number; scheduledClasses: number; activeUsers: number } }>('/public/stats'),

  // Public Holidays & Class Mode (no auth required — for landing page bulletin)
  getPublicHolidays: () => apiCall<{ holidays: any[] }>('/public/holidays'),
  getPublicClassModeSettings: () => apiCall<{ settings: any[] }>('/public/class-mode-settings'),

  // File Upload — Images → Cloudinary, Documents → Supabase Storage
  uploadFile: async (file: File, token: string) => {
    // Convert file to base64 data URI
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    return apiCall<{ success: boolean; url: string; publicId: string; format: string; bytes: number; storage: string }>('/upload', {
      method: 'POST',
      body: { file: base64, fileName: file.name },
      token,
    });
  },

  // Announcements
  getAnnouncements: () => apiCall<{ announcements: any[] }>('/announcements'),
  createAnnouncement: (data: any, token: string) => apiCall('/announcements', { method: 'POST', body: data, token }),
  updateAnnouncement: (id: string, data: any, token: string) => apiCall(`/announcements/${id}`, { method: 'PUT', body: data, token }),
  deleteAnnouncement: (id: string, token: string) => apiCall(`/announcements/${id}`, { method: 'DELETE', token }),

  // AI Schedule Generation (admin only)
  generateAISchedules: (data: { semester: number; schoolYear: string; courseId?: string }, token: string) =>
    apiCall<{ success: boolean; schedules: any[]; totalGenerated: number; totalValid: number; message?: string }>('/ai/generate-schedules', { method: 'POST', body: data, token }),

  // K-means cluster snapshots (admin only)
  getClusterSnapshots: (token: string, options: number | ClusterSnapshotQueryOptions = 12) => {
    const resolvedOptions = typeof options === 'number' ? { limit: options } : options;
    const searchParams = new URLSearchParams();

    Object.entries(resolvedOptions).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, String(value));
      }
    });

    return apiCall<{ snapshots: any[]; filters: Record<string, unknown>; count: number }>(
      `/analytics/cluster-snapshots${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
      { token },
    );
  },
  createClusterSnapshot: (data: any, token: string) =>
    apiCall<{ success: boolean; snapshot: any }>('/analytics/cluster-snapshots', { method: 'POST', body: data, token }),

  // Document Comments
  getComments: (documentId: string, token: string) =>
    apiCall<{ comments: any[] }>(`/comments/${documentId}`, { token }),
  createComment: (documentId: string, data: { content: string; parentId?: string }, token: string) =>
    apiCall<{ comment: any }>(`/comments/${documentId}`, { method: 'POST', body: data, token }),
  updateComment: (documentId: string, commentId: string, data: { content: string }, token: string) =>
    apiCall<{ comment: any }>(`/comments/${documentId}/${commentId}`, { method: 'PUT', body: data, token }),
  deleteComment: (documentId: string, commentId: string, token: string) =>
    apiCall(`/comments/${documentId}/${commentId}`, { method: 'DELETE', token }),

  // Holidays
  getHolidays: (token: string) => apiCall<{ holidays: any[] }>('/holidays', { token }),
  createHoliday: (data: any, token: string) => apiCall('/holidays', { method: 'POST', body: data, token }),
  updateHoliday: (id: string, data: any, token: string) => apiCall(`/holidays/${id}`, { method: 'PUT', body: data, token }),
  deleteHoliday: (id: string, token: string) => apiCall(`/holidays/${id}`, { method: 'DELETE', token }),

  // Class Mode Settings
  getClassModeSettings: (token: string) => apiCall<{ settings: any[] }>('/class-mode-settings', { token }),
  createClassModeSetting: (data: any, token: string) => apiCall('/class-mode-settings', { method: 'POST', body: data, token }),
  updateClassModeSetting: (id: string, data: any, token: string) => apiCall(`/class-mode-settings/${id}`, { method: 'PUT', body: data, token }),
  deleteClassModeSetting: (id: string, token: string) => apiCall(`/class-mode-settings/${id}`, { method: 'DELETE', token }),

  // Debug endpoints (admin only)
  debugScheduleFilters: (academicYear: string, semester: string, token: string) =>
    apiCall<any>(`/debug/schedule-filters?academicYear=${encodeURIComponent(academicYear)}&semester=${encodeURIComponent(semester)}`, { token }),

  // Notifications
  getNotifications: (token: string) => apiCall<{ notifications: any[] }>('/notifications', { token }),
  markNotificationRead: (id: string, token: string) => apiCall(`/notifications/${id}/read`, { method: 'PUT', token }),
  markAllNotificationsRead: (token: string) => apiCall('/notifications/read-all', { method: 'PUT', token }),
  deleteNotification: (id: string, token: string) => apiCall(`/notifications/${id}`, { method: 'DELETE', token }),
};